// Vendor portal (ERP-PLAN §5.8, migration 048): the rules the portal's own screens (web/src/portal/) and its server
// (server/src/portal/) share. No zod here, so the portal's page stays small: what a vendor may send is
// shared/portal-fields.ts. Staff writes are mutations (shared/mutations-portal.ts).
export const PORTAL_LANGS = ["en", "zh"] as const;
export type PortalLang = (typeof PORTAL_LANGS)[number];

/** A portal sign-in link lasts a week and signs in once; a session lasts 90 days. */
export const LINK_DAYS = 7;
export const PORTAL_SESSION_DAYS = 90;
/** Photos per part or product. */
export const MAX_PHOTOS = 12;

/** A SKU as compared for repeats: no case, no spaces at the ends. */
export const skuKey = (sku: string) => sku.trim().toLowerCase();

/** What a vendor product's name becomes on the part link it's linked to (the name POs print): "Shell (Black)". */
export const linkedName = (p: { name: string; variant: string }) => (p.variant.trim() ? `${p.name.trim()} (${p.variant.trim()})` : p.name.trim());

// Vendor prices (migration 054; Mitch, 2026-11-02: "Vendors should be able to set their pricing for items") ---------

/** A price for a quantity and up: the first break is the price. */
export type Break = { min_qty: number; unit_price: number };
/** A part's price as the portal shows and compares it: currency, breaks by quantity, MOQ and lead time. */
export type PriceTerms = { currency: string; price_breaks: Break[]; moq: number | null; lead_time_days: number | null };
/** What a vendor proposes: the terms, from a day if they say, with a word for HHL. */
export type PriceProposal = PriceTerms & { effective_from: string | null; note: string };
export const PRICE_STATUSES = ["waiting", "accepted", "declined", "withdrawn", "replaced"] as const;
export type PriceStatus = (typeof PRICE_STATUSES)[number];

const n = (v: unknown) => (v == null || v === "" ? null : Number(v));

/** Breaks as numbers, by quantity, a quantity once (the later one wins), none without a price. */
export function cleanBreaks(breaks: unknown): Break[] {
  const out = new Map<number, number>();
  for (const b of Array.isArray(breaks) ? (breaks as Record<string, unknown>[]) : []) {
    const qty = n(b?.min_qty);
    const price = n(b?.unit_price);
    if (qty != null && qty > 0 && price != null && price >= 0) out.set(qty, price);
  }
  return [...out].sort((a, b) => a[0] - b[0]).map(([min_qty, unit_price]) => ({ min_qty, unit_price }));
}

/**
 * A link's price as terms (vendor_item, or a proposal's `was`): its breaks, else its one price from its MOQ (or 1). A
 * link with no price has no breaks.
 */
export function termsOf(link: { unit_price: unknown; currency: string; price_breaks: unknown; moq: unknown; lead_time_days: unknown }): PriceTerms {
  const breaks = cleanBreaks(link.price_breaks);
  const price = n(link.unit_price);
  const moq = n(link.moq);
  return {
    currency: link.currency || "USD",
    price_breaks: breaks.length ? breaks : price == null ? [] : [{ min_qty: moq && moq > 0 ? moq : 1, unit_price: price }],
    moq: moq && moq > 0 ? moq : null,
    lead_time_days: n(link.lead_time_days),
  };
}

/** Whether two prices are the same (currency, every break, MOQ and lead time). */
export const sameTerms = (a: PriceTerms, b: PriceTerms) =>
  a.currency === b.currency &&
  a.moq === b.moq &&
  a.lead_time_days === b.lead_time_days &&
  JSON.stringify(cleanBreaks(a.price_breaks)) === JSON.stringify(cleanBreaks(b.price_breaks));

/** "$1.95", "CNY 12.5": a price as both sides read it. */
export function money(price: number | string | null | undefined, currency = "USD") {
  const v = n(price);
  if (v == null) return "";
  const text = v.toLocaleString("en-US", { maximumFractionDigits: 4, minimumFractionDigits: v % 1 ? 2 : 0 });
  return currency === "USD" ? `$${text}` : `${currency} ${text}`;
}

/** "500+ $1.95 · 2,000+ $1.80": the breaks on one line. */
export const breaksText = (t: Pick<PriceTerms, "currency" | "price_breaks">) =>
  t.price_breaks.map((b) => `${b.min_qty.toLocaleString("en-US")}+ ${money(b.unit_price, t.currency)}`).join(" · ");
